import { Injectable } from '@angular/core';
import { Observable, Subject, map, tap } from 'rxjs';
import { Connect } from '../classes/connect';
import { ApiResponse } from '../interfaces/api-response';
import { AssistantChatResponse, AssistantPeriod } from '../components/assistant/assistant.store';
import { ConnectServerService } from './connect-server.service';

export type AssistantDockPosition = 'bottom_left' | 'bottom_right' | 'top_left' | 'top_right';

export interface AssistantConfig {
  company_enabled: number;
  allowed: number;
  enabled: number;
  visible: number;
  topics: string[];
  company_topics: string[];
  position: AssistantDockPosition;
  header_hidden: number;
}

export interface AssistantCompanyUser {
  id: number;
  name: string;
  email: string;
  allowed: number;
  enabled: number;
}

export interface AssistantCompanySettings {
  company_enabled: number;
  topics: string[];
  catalog: string[];
  users: AssistantCompanyUser[];
}

@Injectable({
  providedIn: 'root',
})
export class AssistantConfigService {
  private readonly preferenceSaved = new Subject<AssistantConfig>();
  readonly savedPreference$ = this.preferenceSaved.asObservable();

  constructor(private connectServerService: ConnectServerService) {}

  getConfig(): Observable<AssistantConfig> {
    return this.connectServerService
      .getRequestLara<ApiResponse<AssistantConfig>>(Connect.urlServerLaraApi, 'assistant/config', {})
      .pipe(map((res) => this.normalizeConfig(res?.data)));
  }

  ask(payload: {
    message: string;
    language: 'it' | 'en';
    period: AssistantPeriod;
  }): Observable<ApiResponse<AssistantChatResponse>> {
    return this.connectServerService.postRequestCookie('assistant/ask', payload);
  }

  savePreference(
    enabled: number,
    position: AssistantDockPosition,
    headerHidden: number,
  ): Observable<AssistantConfig> {
    return this.connectServerService
      .postRequest<ApiResponse<AssistantConfig>>(Connect.urlServerLaraApi, 'assistant/savePreference', {
        enabled,
        position,
        header_hidden: headerHidden,
      })
      .pipe(
        map((res) => this.normalizeConfig(res?.data)),
        tap((config) => this.preferenceSaved.next(config)),
      );
  }

  getCompanySettings(): Observable<AssistantCompanySettings> {
    return this.connectServerService
      .getRequestLara<ApiResponse<AssistantCompanySettings>>(Connect.urlServerLaraApi, 'settings/company/assistant', {})
      .pipe(map((res) => this.normalizeCompany(res?.data)));
  }

  saveCompany(companyEnabled: number, topics: string[]): Observable<AssistantCompanySettings> {
    return this.connectServerService
      .postRequest<ApiResponse<AssistantCompanySettings>>(Connect.urlServerLaraApi, 'settings/company/saveAssistant', {
        company_enabled: companyEnabled,
        topics,
      })
      .pipe(map((res) => this.normalizeCompany(res?.data)));
  }

  saveCompanyUser(userId: number, allowed: number, enabled: number): Observable<AssistantCompanySettings> {
    return this.connectServerService
      .postRequest<ApiResponse<AssistantCompanySettings>>(Connect.urlServerLaraApi, 'settings/company/saveAssistantUser', {
        user_id: userId,
        allowed,
        enabled,
      })
      .pipe(map((res) => this.normalizeCompany(res?.data)));
  }

  private normalizeConfig(data: AssistantConfig | null | undefined): AssistantConfig {
    return {
      company_enabled: data?.company_enabled === 1 ? 1 : 0,
      allowed: data?.allowed === 1 ? 1 : 0,
      enabled: data?.enabled === 1 ? 1 : 0,
      visible: data?.visible === 1 ? 1 : 0,
      topics: Array.isArray(data?.topics) ? data.topics : [],
      company_topics: Array.isArray(data?.company_topics) ? data.company_topics : [],
      position: this.normalizePosition(data?.position),
      header_hidden: data?.header_hidden === 0 ? 0 : 1,
    };
  }

  private normalizePosition(value: unknown): AssistantDockPosition {
    if (value === 'bottom_right' || value === 'top_left' || value === 'top_right' || value === 'bottom_left') {
      return value;
    }
    return 'bottom_left';
  }

  private normalizeCompany(data: AssistantCompanySettings | null | undefined): AssistantCompanySettings {
    return {
      company_enabled: data?.company_enabled === 1 ? 1 : 0,
      topics: Array.isArray(data?.topics) ? data.topics : [],
      catalog: Array.isArray(data?.catalog) ? data.catalog : [],
      users: Array.isArray(data?.users) ? data.users : [],
    };
  }
}
